import { TaskTracker } from "../stores/budgetReview/budgetReview.interface";
import { convertUSDToNumber } from "../utils/formatChecks";
import { taskNames } from "../stores/tasks/Tasks.action";
import { budgetReviewStatusEnum } from "../ilp/list-view/BudgetTaskManagement";
import { getTimeFromDate } from "../utils/utils";

export function convertNumberToUSD(input: number | string): string {
  const number = typeof input === "number" ? input : parseFloat(input);
  if (isNaN(number)) {
    return "";
  }
  const fixedNumber = number.toFixed(2);
  const parts = fixedNumber.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `$${parts.join(".")}`;
}

export const getDate = (date: string, format?: string) => {
  const formattedDate = getTimeFromDate(date, format || "MMM DD, YYYY, HH:mm");
  return formattedDate;
};

export const addEmptyLineItems = (
  row: any,
  fieldsData: any[],
  withNullAsId?: any,
  unitVal?: string | number
) => {
  const newFields = [];

  for (let i = 0; i < row.val; i++) {
    const newField = {
      id: withNullAsId ? null : `tempId_${Math.random()}`,
      unit: unitVal || 1,
      item: "",
      description: "",
      financedBudget: withNullAsId ? 0 : "$0.00",
      budget: withNullAsId ? 0 : "$0.00",
      StandardizedItem: "",
      standardizedItemId: null
    };

    newFields.push(newField);
  }

  return [...fieldsData, ...newFields];
};

// Prefer BudgetDocs.filter directly in new call sites. Re-exported here so
// existing imports (and tests) keep working without the old "latest-first"
// filter that dropped SCOW/REBD docs when a newer non-budget doc shared the
// same PROPERTY asset.
export { filterObjectsByTag } from "./BudgetDocs.filter";

export function sortArrayByUpdatedOn(arr: TaskTracker[]): TaskTracker[] {
  if (!arr?.length) return [];
  return arr.sort((a, b) => {
    const dateA = new Date(a.updatedOn);
    const dateB = new Date(b.updatedOn);
    return dateB.getTime() - dateA.getTime();
  });
}

export function checkPropertyMapped(obj: any): boolean {
  // Check if the object has the "propertyId" field
  if (
    obj.staticData?.documentData &&
    Object.prototype.hasOwnProperty.call(
      obj.staticData.documentData,
      "propertyId"
    )
  ) {
    // Check if the value of "propertyId" is not null and not undefined
    return obj.staticData?.documentData?.propertyId !== null;
  }
  return false;
}

export const transformToReduxList = (arr: any[]) =>
  arr.map((item: any) => ({
    budgetLineItemOrder: item.budgetLineItemOrder,
    budgetLineItemId: item.budgetLineItemId,
    item: item.item,
    description: item.description,
    // budget: item.budget?.replace(/[\$,]/g, "") || item.budget, // Remove "$" and commas to keep it numeric
    budget: `${convertUSDToNumber(item.budget)}`,
    financedBudget: `${convertUSDToNumber(item.financedBudget)}`,
    // item.financedBudget?.replace(/[\$,]/g, "") || item.financedBudget, // Remove "$" and commas to keep it numeric
    commentThreadId: item.commentThreadId,
    unit: item.unit,
    comments: null // Set comments to null
  }));

export function isBudgetThereAndNotStarted(
  tasks: any[],
  isDSCR: boolean
): boolean {
  if (isDSCR) return false;

  const assignedTasks = tasks?.[0]?.assignedTasks || [];

  return assignedTasks.some(
    (task: any) =>
      task.taskName === taskNames.BUDGET_REVIEW &&
      [
        budgetReviewStatusEnum.LoanSubmitted,
        budgetReviewStatusEnum.BudgetMissing
      ].includes(task.taskStatus)
  );
}

/**
 * Approve stays disabled when the user is not an eligible assignee in a review
 * status, or when OCR has not returned any budget document info for the loan.
 */
export function isBudgetApprovalActionDisabled({
  canActOnApproval,
  isBudgetInReviewStatus,
  hasBudgetPropData
}: {
  canActOnApproval: boolean;
  isBudgetInReviewStatus: boolean;
  hasBudgetPropData: boolean;
}): boolean {
  if (!hasBudgetPropData) return true;
  if (canActOnApproval) return !isBudgetInReviewStatus;
  return true;
}

/**
 * Tagged budget doc is present but OCR never produced budget-loan /
 * extraction document info — needs bootstrap before manual entry can persist.
 */
export function isBudgetOcrDataUnavailable({
  hasTaggedBudgetDoc,
  hasBudgetPropData,
  isExtractionLoading
}: {
  hasTaggedBudgetDoc: boolean;
  hasBudgetPropData: boolean;
  isExtractionLoading: boolean;
}): boolean {
  return Boolean(
    hasTaggedBudgetDoc && !hasBudgetPropData && !isExtractionLoading
  );
}

/** Resolve property id for budget-loan create from tagged doc / header / loan props. */
export function resolveBudgetPropertyId({
  selectedBudgetDoc,
  selectedPropId,
  propertyDetails
}: {
  selectedBudgetDoc?: any;
  selectedPropId?: string | number | null;
  propertyDetails?: any[];
}): string | number | null {
  const propertyAsset = (selectedBudgetDoc?.assets || []).find(
    (asset: { assetName?: string }) => asset.assetName === "PROPERTY"
  );
  if (propertyAsset?.assetExternalId != null && propertyAsset.assetExternalId !== "") {
    return propertyAsset.assetExternalId;
  }
  if (selectedPropId != null && selectedPropId !== "") {
    return selectedPropId;
  }
  const first = propertyDetails?.[0];
  return first?.loanPropertyOrder ?? first?.propertyId ?? null;
}

/** Request body for POST /ocr/budget-loan/create/{source}. */
export function buildBudgetLoanCreatePayload({
  loanId,
  externalDocumentId,
  propertyId,
  submittedTotalRehabBudget,
  submittedFinancedBudget,
  budgetApprovalStatus
}: {
  loanId: string;
  externalDocumentId: string | number;
  propertyId: string | number;
  submittedTotalRehabBudget?: string | number;
  submittedFinancedBudget?: string | number;
  budgetApprovalStatus?: string;
}) {
  return {
    budgetLoanInfo: {
      toorakLoanId: loanId,
      budgetApprovalStatus: budgetApprovalStatus || "In Progress",
      submittedTotalRehabBudget: String(submittedTotalRehabBudget ?? "0"),
      submittedFinancedBudget: String(submittedFinancedBudget ?? "0"),
      grandCalculatedTotalBudget: "0"
    },
    budgetDocumentInfo: [
      {
        externalDocumentId: String(externalDocumentId),
        propertyId: String(propertyId),
        budgetStatus: "ACTIVE",
        version: 1,
        extractionStatus: "PREDICTION_FAILED",
        selectionMode: "CONSOLIDATED",
        calculatedTotalBudget: "0",
        calculatedTotalFinancedBudget: "0",
        budgetLineItemList: []
      }
    ]
  };
}
